<template>
  <div :class="['statistics', {'no-min-height': removeMinHeight}]">
    <div class="statistics__header">
      <div class="statistics__header-head">
        <a v-if="districtIsSelected" href="#" @click.prevent="$emit('toggleDistrict', null)">
          {{ demographic.Title.Naam._text }}
        </a>
        <h1 v-if="districtIsSelected && district"><strong>{{ districtVoteStatus }}</strong> {{ selectedDistrict.Naam._text }} ({{ $numberWithDots(selectedDistrict.AantalInwoners._text) }} inwoners)</h1>
        <h1 v-else><strong>{{ demographic.Status._text }}</strong> {{ demographic.Title.Naam._text }} ({{ $numberWithDots(demographic.AantalInwoners._text) }} inwoners) </h1>
      </div>

      <div class="statistics__header-updater">
        <div class="statistics__header-updater-container">
          <strong>Laatst bijgewerkt op: {{ $dateToTime(lastUpdated.rss, {showSeconds: true}) }}</strong>
          <Spinner v-if="loading.updating" :width="20"/>
        </div>
      </div>
    </div> 
    <!-- No data is parsed to the graphs. everything is being fetched from the store -->
    <GraphOne v-if="queryArgs.graphOne !== '0'" class="statistics__graph one" />
    <div :class="['statistics__graph-row', {
      'hide-row': hideSecondRow
    }]">
      <GraphTwo v-if="queryArgs.graphTwo !== '0'" class="statistics__graph two" />
      <GraphThree v-if="queryArgs.graphThree !== '0'" class="statistics__graph three" />
    </div>
  </div>
</template>

<script>
import PieChart from "./charts/PieChart.vue";
import HorBar from "./charts/HorBar.vue";
import BarChart from "./charts/BarChart.vue";
import Spinner from "./Spinner.vue";
import Checkbox from "./elements/Checkbox.vue";
import GraphOne from "./stats/GraphOne.vue";
import GraphTwo from "./stats/GraphTwo.vue";
import GraphThree from "./stats/GraphThree.vue";
import { pushToast } from "../services/toaster.js";
import { mapActions, mapState } from "vuex";
export default {
  components: {
    PieChart,
    HorBar,
    BarChart,
    Spinner,
    Checkbox,
    GraphOne,
    GraphTwo,
    GraphThree
  },
  props: {
    district: { type: Object, default: null }
  },
  computed: {
    ...mapState({
      demographic: state => state.feed.provincialData.demographic,
      selectedDistrict: state => state.feed.selectedDistrict,
      seats: state => state.feed.provincialData.seats,
      loading: state => state.feed.loading,
      lastUpdated: state => state.feed.lastUpdated,
      queryArgs: state => state.route.query
    }),

    districtVoteStatus() {
      const text = this.district["media:uitslagstatus"]._text;
      return text.charAt(0).toUpperCase() + text.slice(1);
    },

    districtIsSelected() {
      return Object.keys(this.selectedDistrict).length;
    },
    hideSecondRow() {
      return (
        this.queryArgs.graphTwo === "0" && this.queryArgs.graphThree === "0"
      );
    },
    removeMinHeight() {
      return this.hideSecondRow || this.queryArgs.graphOne === "0";
    }
  },
  watch: {
    district: {
      // init data fetch when selecting new district
      async handler() {
        if (this.district) {
          this.getDistrictStats(this.district.guid._text).catch(() => {
            pushToast(
              "failed",
              `"De data kon niet worden opgehaald. Controleer de internet verbinding!" `
            );
          });
        } else {
          // reset to prevent graph glitching
          this.resetDistrictStats();
        }
      }
    }
  },
  methods: {
    ...mapActions("feed", ["getDistrictStats", "resetDistrictStats"])
  }
};
</script>

<style lang="scss">
@import "../util.scss";

.statistics {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 80px);
  max-width: 1200px;
  min-height: 840px;
  margin: 0 auto;
  padding: 12px 0 20px 20px;

  &.no-min-height {
    min-height: 0;
  }

  @include respond-to("large") {
    padding: 12px 20px 20px 20px;
  }
  @include respond-to("small") {
    min-height: 0;
  }

  h1 {
    margin: 0 12px 0 0;
    font-weight: 500;
    font-size: 23px;

    @include respond-to("medium-small") {
      width: 100%;
      margin-bottom: 12px;
      font-size: 19px;
    }
  }

  &__graph {
    background-color: white;
    position: relative;
    padding: 20px;
    margin: 12px;
    box-shadow: 1px 1px 8px -2px rgba(0, 0, 0, 0.4);

    &.district {
      &.two {
        padding-right: 30px;
        width: 400px;
      }
      & + .three {
        // max-width: 600px;
        width: calc(100% - 400px);
      }
      .statistics__graph-pie {
        &--two {
          max-height: 230px;
          left: 0;
          margin: 0;
          right: auto;
          width: 100%;
        }
      }
    }

    h2 {
      margin-bottom: 10px;
    }
  }

  &__loader-wrapper {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  }

  &__expand {
    display: block;
    width: 42px;
    height: 42px;
    position: absolute;
    right: 6px;
    top: 10px;
    padding: 12px;
    cursor: pointer;

    &:after {
      content: "";
      width: 18px;
      height: 18px;
      position: absolute;

      display: block;
      background: {
        image: url("../assets/expand-button.svg");
        size: cover;
      }
    }
  }

  &__header {
    width: 100%;
    height: auto;
    padding: 0 12px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 8px;

    @include respond-to("medium-small") {
      flex-wrap: wrap;
    }

    &-head {
      a {
        display: flex;
        font-weight: bold;
        color: black;
        text-decoration: none;
        margin-bottom: 12px;
        &:before {
          content: "";
          width: 15px;
          height: 15px;
          margin-right: 6px;
          background: {
            image: url("../assets/left-arrow.svg");
            size: cover;
          }
        }
      }
    }

    &-updater-container {
      display: flex;
      align-items: center;
    }

    &-updater {
      display: flex;
      align-items: center;
      strong {
        margin-right: 12px;
      }
      &-toggle {
        margin-left: 8px;
      }
      @include respond-to("medium-small") {
        justify-content: space-between;
        width: 100%;
      }
    }
  }

  &__turnup {
    display: flex;
    flex-direction: column;
    .statistics__graphs-container {
      position: absolute;
      left: 12px;
      right: 18px;
      top: 60px;
    }

    &-table {
      width: 100%;
      margin-top: 180px;
      margin-bottom: 12px;
      border-spacing: 8px;
      text-align: center;
      thead td {
        font-weight: bold;
        @include truncate();
      }
      tr {
        &:nth-of-type(1) {
          td {
            .legend {
              border: 3px solid $primary;
            }
          }
        }
        &:nth-of-type(2) {
          td {
            .legend {
              border: 3px solid grey !important;
            }
          }
        }
      }
      tr td {
        .legend {
          width: 12px;
          height: 12px;
          border-radius: 50%;
        }
        font-size: 13px;
      }
    }
  }

  .one {
    width: calc(100% - 24px);
    flex: 1;
    min-height: 350px;
    max-height: 500px;
  }

  .two {
    align-self: flex-start;
    max-height: 350px;
    height: 100%;
    width: 490px;
    display: flex;
    flex-direction: column;
  }

  .three {
    max-height: 350px;
    height: 100%;
    align-self: flex-start;
    width: calc(100% - 490px);
    @include respond-to("small") {
      height: auto;
    }
  }

  .two,
  .three {
    @include respond-to("small") {
      width: 100% !important;
      min-height: 340px !important;
    }
  }

  &__graph-row {
    flex: 1;
    display: flex;
    &.hide-row {
      display: none;
    }
    @include respond-to("small") {
      flex-wrap: wrap;
    }
  }

  .live-stats {
    display: flex;
    height: calc(100% - 30px);
    flex-direction: column;
    &__item {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      text-align: center;
      border-bottom: 1px solid $border-medium;

      &:last-child {
        border: 0;
      }

      &-head {
        font-size: 17px;
      }
      &-value {
        font-size: 16px;
        margin-top: 6px;
      }
    }
  }
  .statistics__graph-pie-container {
    position: relative;
    height: 100%;
  }
  .statistics__graph-pie {
    position: absolute;
    height: 100%;
    max-height: 300px;
    width: calc(50% - 32px);
    left: 20px;
    top: 50%;

    transform: translateY(-50%);
    &--one {
      left: 20px;
      right: calc(50% - 12px);
      margin-right: 12px;
    }
    &--two {
      left: 50%;
      margin-left: 12px;
      right: calc(100% - 20px);
    }

    .chart {
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
    }
  }

  &__upper-graph {
    position: absolute;
    left: 12px;
    right: 12px;
    top: 50px;
    bottom: 12px;
  }
  &__upper-graph-header {
    display: flex;
    align-items: center;
    padding-right: 36px;

    h2 {
      margin: 0;
      max-width: 60%;
      @include truncate();

      @include respond-to("medium-small") {
        font-size: 15px;
        max-width: 40%;
      }

      span {
        font-size: 18px;
      }
    }
  }
  &__upper-graph-toggle {
    font-weight: bold;
    cursor: pointer;
    padding-left: 16px;
    margin-left: 16px;
    border-left: 1px solid $border-medium;
  }
  &__upper-graph-no-results {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
}
</style>
